import type {
  RankedSearchResult,
  SearchDocument,
  SemanticResult,
} from './searchTypes';

const RRF_CONSTANT = 60;
const QUESTION_SEMANTIC_WEIGHT = 1.75;

export function isQuestionShaped(query: string): boolean {
  return (
    /^(?:who|what|when|where|why|how|should|can|could|does|do|did|is|are|will|would)\b/iu.test(
      query.trim(),
    ) || query.trim().endsWith('?')
  );
}

export function reciprocalRankFusion(
  lexical: readonly RankedSearchResult[],
  semantic: readonly SemanticResult[],
  documents: readonly SearchDocument[],
  limit = 50,
  query = '',
): RankedSearchResult[] {
  const documentsByParagraph = new Map(
    documents.map((document) => [document.paragraphId, document]),
  );
  const merged = new Map<
    string,
    {
      document: SearchDocument;
      score: number;
      sources: Set<'lexical' | 'semantic'>;
      matchedTypes: RankedSearchResult['matchedTypes'];
      semanticScore?: number;
    }
  >();

  for (const result of lexical) {
    merged.set(result.document.paragraphId, {
      document: result.document,
      score: 1 / (RRF_CONSTANT + result.rank),
      sources: new Set(['lexical']),
      matchedTypes: result.matchedTypes,
    });
  }
  const semanticWeight = isQuestionShaped(query)
    ? QUESTION_SEMANTIC_WEIGHT
    : 1;
  for (const [index, result] of semantic.entries()) {
    const document = documentsByParagraph.get(result.paragraphId);
    if (!document) continue;
    const current = merged.get(result.paragraphId) ?? {
      document,
      score: 0,
      sources: new Set<'lexical' | 'semantic'>(),
      matchedTypes: [],
    };
    current.score += semanticWeight / (RRF_CONSTANT + index + 1);
    current.sources.add('semantic');
    current.semanticScore = result.score;
    merged.set(result.paragraphId, current);
  }

  return [...merged.values()]
    .sort(
      (first, second) =>
        second.score - first.score ||
        first.document.id.localeCompare(second.document.id),
    )
    .slice(0, limit)
    .map((result, rank) => ({
      document: result.document,
      score: result.score,
      rank: rank + 1,
      sources: [...result.sources],
      matchedTypes: result.matchedTypes,
      semanticScore: result.semanticScore,
    }));
}
